import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import organizationService from "./organizationService";
export const getOrganizations = createAsyncThunk(
  "organizations/getOrganizations",
  async (params = {}, thunkAPI) => {
    try {
      return await organizationService.getOrganizations(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const getOrganizationById = createAsyncThunk(
  "organizations/getOrganizationById",
  async (id, thunkAPI) => {
    try {
      return await organizationService.getOrganizationById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const createOrganization = createAsyncThunk(
  "organizations/createOrganization",
  async (data, thunkAPI) => {
    try {
      return await organizationService.createOrganization(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const updateOrganization = createAsyncThunk(
  "organizations/updateOrganization",
  async ({ id, data }, thunkAPI) => {
    try {
      return await organizationService.updateOrganization({ id, data });
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const deleteOrganization = createAsyncThunk(
  "organizations/deleteOrganization",
  async (id, thunkAPI) => {
    try {
      return await organizationService.deleteOrganization(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const getOrganizationCoaches = createAsyncThunk(
  "organizations/getOrganizationCoaches",
  async ({ id, params = {} }, thunkAPI) => {
    try {
      return await organizationService.fetchOrganizationCoaches(id, params);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const getMyOrganizationCoaches = createAsyncThunk(
  "organizations/getMyOrganizationCoaches",
  async (_, thunkAPI) => {
    try {
      return await organizationService.getMyOrganizationCoaches();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

const initialState = {
  organizations: [],
  selectedOrganization: null,
  organizationCoaches: [],

  pagination: {
    page: 1,
    limit: 12,
    total: 0,
    pages: 1,
  },

  filters: {
    search: "",
    is_active: "",
  },

  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isLoadingCoaches: false,

  error: null,
};

const organizationSlice = createSlice({
  name: "organizations",

  initialState,

  reducers: {
    clearOrganizationError: (state) => {
      state.error = null;
    },

    setOrganizationFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };

      state.pagination.page = 1;
    },

    setOrganizationPage: (state, action) => {
      state.pagination.page = action.payload;
    },

    clearSelectedOrganization: (state) => {
      state.selectedOrganization = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations =
          action.payload?.data?.items || action.payload?.data || [];
        state.pagination =
          action.payload?.data?.pagination ||
          action.payload?.pagination ||
          initialState.pagination;
      })

      .addCase(getOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(getOrganizationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getOrganizationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedOrganization = action.payload?.data || null;
      })

      .addCase(getOrganizationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createOrganization.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })

      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isCreating = false;

        if (action.payload?.data) {
          state.organizations.unshift(action.payload.data);
        }
      })

      .addCase(createOrganization.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      .addCase(updateOrganization.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updated = action.payload?.data;

        if (updated) {
          state.organizations = state.organizations.map((org) =>
            org._id === updated._id ? { ...org, ...updated } : org,
          );

          if (state.selectedOrganization?._id === updated._id) {
            state.selectedOrganization = {
              ...state.selectedOrganization,
              ...updated,
            };
          }
        }
      })

      .addCase(updateOrganization.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      .addCase(deleteOrganization.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })

      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.isDeleting = false;

        const deletedId =
          action.payload?.data?.deleted_organization_id || action.meta.arg;

        state.organizations = state.organizations.filter(
          (org) => org._id !== deletedId,
        );

        if (state.selectedOrganization?._id === deletedId) {
          state.selectedOrganization = null;
        }
      })

      .addCase(deleteOrganization.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      .addCase(getOrganizationCoaches.pending, (state) => {
        state.isLoadingCoaches = true;
        state.error = null;
      })

      .addCase(getOrganizationCoaches.fulfilled, (state, action) => {
        state.isLoadingCoaches = false;
        state.organizationCoaches =
          action.payload?.data?.items || action.payload?.data || [];
      })

      .addCase(getOrganizationCoaches.rejected, (state, action) => {
        state.isLoadingCoaches = false;
        state.error = action.payload;
      })

      .addCase(getMyOrganizationCoaches.pending, (state) => {
        state.isLoadingCoaches = true;
        state.error = null;
      })

      .addCase(getMyOrganizationCoaches.fulfilled, (state, action) => {
        state.isLoadingCoaches = false;
        state.organizationCoaches =
          action.payload?.data?.items || action.payload?.data || [];
      })

      .addCase(getMyOrganizationCoaches.rejected, (state, action) => {
        state.isLoadingCoaches = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearOrganizationError,
  setOrganizationFilters,
  setOrganizationPage,
  clearSelectedOrganization,
} = organizationSlice.actions;

export default organizationSlice.reducer;
